import React, { useEffect, useState } from 'react';
import { Skeleton, Modal } from 'antd';
import { UserFilter, User } from 'types';
import { useDispatch, useSelector } from 'react-redux';

import history from 'app-router/history';
import UserList from 'components/templates/user-list';
import ButtonPrimary from 'components/molecules/button-primary';
import Layout from 'components/templates/layout';
import FormSearchUser from 'components/templates/form-search-user';
import { useIntl } from 'common/config/i18n';
import {
  UsersActions,
  UsersSelectors,
  UserActions,
  UserSelectors,
  UserInfoSelectors,
  ClearStateSliceActions,
  ProfilesActions,
  GroupsActions,
  ProfilesSelectors,
  ServiceTypesSelectors,
} from 'common/config/store';
import ModalContentConfirm from 'components/organisms/modal-content-confirm';
import { StyledMainTitle, StyledListUtil } from 'common/styling/mixins';
import { isRequestError } from 'common/utils/error';
import MessageModal from 'components/organisms/message-modal';
import useFromMenu from 'hooks/use-from-menu';

export interface State {
  id?: string;
  userName?: string;
}

const { StyledListWrapper, StyledListTitleWrapper, StyledListButtonWrapper, StyledListBoldWrapper } = StyledListUtil;

const UsersList: React.FC<State> = () => {
  const dispatch = useDispatch();
  const { t } = useIntl();

  const [modalVisible, setModalVisible] = useState(false);
  const [user, setUserinfo] = useState({ id: '', userName: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const status = useSelector(UsersSelectors.statusSelector);
  const canWriteUsers = useSelector(UserInfoSelectors.hasWriteAccess('Users'));
  const canReadUsers = useSelector(UserInfoSelectors.hasReadAccess('Users'));
  const StatusUser = useSelector(UserInfoSelectors.statusSelector);
  const error = useSelector(UserSelectors.errorSelector);
  const errorType = useSelector(UserSelectors.errorTypeSelector);
  const usersData = useSelector(UsersSelectors.usersSelector);
  const userInfoData = useSelector(UserInfoSelectors.dataSelector);
  const profils = useSelector(ProfilesSelectors.profilesSelector);
  const profileById = useSelector(ProfilesSelectors.ProfileById);
  const serviceTypes = useSelector(ServiceTypesSelectors.serviceTypesSelector);
  const filterValues = useSelector(UsersSelectors.filtersSelector);
  const isFromMenu = useFromMenu({ clearAfterRead: true });

  useEffect(() => {
    // We wait untill isFromMenu has a value (false or true)
    if (isFromMenu !== null) {
      if (isFromMenu) {
        dispatch(UsersActions.resetFilters());
        dispatch(UsersActions.fetchUsers());
      } else {
        dispatch(UsersActions.fetchUsers(filterValues));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromMenu]);

  useEffect(() => {
    dispatch(ProfilesActions.fetchPofiles());
    dispatch(GroupsActions.fetchGroups());
  }, [dispatch]);

  useEffect(() => {
    if (errorType && errorType === 'delete') setShowDeleteModal(true);
  }, [errorType]);

  const onAddUser = (): void => {
    dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'User' }));
    return history.push('/user');
  };

  const onEditUser = (user: User): void => {
    if (user.userName) {
      return history.push(`/user/${user.userName}`);
    }
  };

  const onDeleteUser = (user: User): void => {
    if (user.id && user.email) {
      setUserinfo({ id: user.id as string, userName: user.email });
      setModalVisible(true);
    }
  };

  const launchAction = (values: UserFilter, action: (params: UserFilter) => void) => {
    dispatch(UsersActions.setFilters(values));
    dispatch(action(values));
  };

  const onDeleteConfirmation = (shouldRemove: boolean) => {
    if (shouldRemove) {
      dispatch(
        UserActions.deleteUser({ id: user.id }, (result) => {
          if (!result.cancelled && !result.error) {
            launchAction(filterValues, UsersActions.fetchUsers);
          }
        })
      );
    }
    setModalVisible(false);
  };

  const fetchListAction = (values: UserFilter) => {
    launchAction(values, UsersActions.fetchUsers);
  };

  const exportListAction = (values: UserFilter) => {
    launchAction(values, UsersActions.exportUsers);
  };

  return (
    <div>
      <Modal
        title={t('Modal', 'USER_MODAL_TITLE')}
        footer={null}
        open={modalVisible}
        onCancel={() => onDeleteConfirmation(false)}
      >
        <ModalContentConfirm
          title={
            <div>
              {t('Modal', 'USER_DELETE_CONFIRM')}
              <StyledListBoldWrapper>{user.userName}</StyledListBoldWrapper>
            </div>
          }
          onConfirm={onDeleteConfirmation}
        />
      </Modal>

      <MessageModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'User' }));
        }}
        errorMessage={error && isRequestError(error) ? undefined : error}
      />

      <Layout />
      <StyledListWrapper>
        <StyledListTitleWrapper>
          <StyledMainTitle>{t('UserPage', 'USER__PAGE_H2')}</StyledMainTitle>
          {canWriteUsers && (
            <StyledListButtonWrapper>
              <ButtonPrimary onClick={onAddUser}>{t('Buttons', 'NEW_USER_CREATION_BUTTON')}</ButtonPrimary>
            </StyledListButtonWrapper>
          )}
        </StyledListTitleWrapper>
        {canReadUsers && (
          <FormSearchUser
            filterValues={filterValues}
            fetchListAction={fetchListAction}
            exportListAction={exportListAction}
            canReadUsers={canReadUsers}
            canWriteUsers={canWriteUsers}
            profils={profils}
            serviceTypes={serviceTypes}
          />
        )}
        {(status === 'loading' || StatusUser === 'loading') && <Skeleton />}
        {(status === 'success' || StatusUser === 'success') && (
          <UserList
            data={usersData}
            canRead={canReadUsers}
            onClickEdit={onEditUser}
            onClickDelete={onDeleteUser}
            fetchListAction={fetchListAction}
            filterValues={filterValues}
            userInfo={userInfoData}
            profileById={profileById}
            serviceTypes={serviceTypes}
          />
        )}
      </StyledListWrapper>
    </div>
  );
};
export default UsersList;
